<script setup lang="ts">
import { ref } from 'vue';

import BaseDialog from '@/components/base/BaseDialog.vue';
import BaseImageCropper from '@/components/base/BaseImageCropper.vue';
import BaseImageUpload, { type ImageType } from '@/components/base/BaseImageUpload.vue';
import type { CroppingInfo } from '@/components/ImageField.vue';
import type { FileInfo, FileVersion } from '@/services/types/file.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';

const uploadImageDialogRef = ref<InstanceType<typeof BaseDialog>>();

// PROPS
const props = defineProps<{
  title: string;
  type: ImageType;
  aspectRatio?: number;
  onSaveImage: (options: SavingOptions) => Promise<void>;
  version: FileVersion;
  file?: FileInfo | null;
  cropping?: CroppingInfo;
}>();

const currentMode = ref<'add' | 'edit'>('add');

// FUNCTIONS
function openDialog(mode: 'add' | 'edit') {
  currentMode.value = mode;
  uploadImageDialogRef.value?.openDialog();
}

function closeDialog() {
  uploadImageDialogRef.value?.closeDialog();
}

async function uploadImage(options: SavingOptions) {
  await props.onSaveImage(options);
  closeDialog();
}

defineExpose({
  openDialog,
  closeDialog,
});
</script>
<template>
  <base-dialog ref="uploadImageDialogRef" class="upload-dialog" persistent>
    <template v-slot:title>
      <div class="upload-dialog__title">{{ props.title }}</div>
    </template>
    <template v-slot:content>
      <base-image-upload
        v-if="props.version === 'v1'"
        :type="props.type"
        :aspect-ratio="aspectRatio"
        :on-save-image="uploadImage"
        @cancel="closeDialog"
      />
      <base-image-cropper
        v-if="props.version === 'v2'"
        :cropping="props.cropping!"
        :file="currentMode === 'add' ? null : props.file"
        :on-cancel="closeDialog"
        :on-save="uploadImage"
      />
    </template>
  </base-dialog>
</template>
<style lang="scss">
.upload-dialog {
  .base-dialog {
    max-width: 800px;
  }
  &__title {
    @include text-h6;
    width: 100%;
    display: flex;
  }
}
</style>
